import {
  GENERATION_MODEL_CATALOG,
  GENERATION_TYPE_LABELS,
  GENERATION_TYPE_TO_CATEGORY,
  computeBaseModelMinPrice,
  getModelGenerationTypes,
} from '@root/shared-generation-pricing-config'
import type {
  GenerationCategory,
  GenerationType,
} from '@root/shared-generation-pricing-config'

// Pure data builders shared by the `/api/docs/*` JSON endpoints and the
// markdown serializers in generation-catalog-markdown.ts — one source of
// truth for both, so the human-facing pages and the AI/crawler-facing
// markdown export can never drift apart.

export interface ModelSummary {
  id: string
  displayName: string
  family: string
  priceUnit: string
  minPrice: number
  generationTypes: Array<string>
  variantCount: number
  variantNames: Array<string>
  variantLabels: Array<string>
}

export interface ModelsListResult {
  total: number
  families: Array<string>
  models: Array<ModelSummary>
}

export function buildModelsList(): ModelsListResult {
  const models = Object.entries(GENERATION_MODEL_CATALOG).map(([id, config]) => ({
    id,
    displayName: config.displayName,
    family: config.family,
    priceUnit: config.priceUnit,
    minPrice: computeBaseModelMinPrice(id),
    generationTypes: getModelGenerationTypes(id),
    variantCount: Object.keys(config.variants).length,
    variantNames: Object.keys(config.variants),
    variantLabels: Object.keys(config.variants).map(
      (t) => GENERATION_TYPE_LABELS[t as GenerationType] ?? t,
    ),
  }))

  const families = [
    ...new Set(Object.values(GENERATION_MODEL_CATALOG).map((c) => c.family)),
  ].filter(Boolean) as Array<string>

  return { total: models.length, families, models }
}

export interface ModelVariantDetail {
  generationType: GenerationType
  pricing: unknown
  supportedParams: Array<string>
  description: string
  featured?: boolean
  generationDisplayName?: string
  priceUnit: string
}

export interface ModelDetailResult {
  id: string
  displayName: string
  family: string
  priceUnit: string
  minPrice: number
  generationTypes: Array<string>
  variants: Array<ModelVariantDetail>
}

export function buildModelDetail(modelId: string): ModelDetailResult | null {
  const model = GENERATION_MODEL_CATALOG[modelId]
  if (!model) return null

  const variants = Object.entries(model.variants)
    .filter(([, v]) => v !== undefined)
    .map(([type, variant]) => ({
      generationType: type as GenerationType,
      pricing: variant.pricing,
      supportedParams: variant.supportedParams,
      description: variant.description,
      featured: variant.featured,
      generationDisplayName: variant.generationDisplayName,
      priceUnit: variant.priceUnit ?? model.priceUnit,
    }))

  return {
    id: modelId,
    displayName: model.displayName,
    family: model.family,
    priceUnit: model.priceUnit,
    minPrice: computeBaseModelMinPrice(modelId),
    generationTypes: getModelGenerationTypes(modelId),
    variants,
  }
}

export interface GenerationTypeInfo {
  type: GenerationType
  label: string
  category: GenerationCategory
  models: Array<string>
  modelCount: number
  families: Array<string>
  params: Array<string>
}

export interface GenerationTypesResult {
  categories: Array<{ category: string; types: Array<GenerationTypeInfo> }>
  totalTypes: number
}

export function buildGenerationTypes(): GenerationTypesResult {
  const typeMap = new Map<GenerationType, { models: Array<string>; families: Array<string> }>()

  for (const [modelId, config] of Object.entries(GENERATION_MODEL_CATALOG)) {
    for (const type of Object.keys(config.variants) as Array<GenerationType>) {
      if (!typeMap.has(type)) typeMap.set(type, { models: [], families: [] })
      const entry = typeMap.get(type)!
      entry.models.push(modelId)
      if (!entry.families.includes(config.family)) entry.families.push(config.family)
    }
  }

  const typeParams = new Map<GenerationType, Set<string>>()
  for (const config of Object.values(GENERATION_MODEL_CATALOG)) {
    for (const [type, variant] of Object.entries(config.variants)) {
      if (!variant) continue
      if (!typeParams.has(type as GenerationType)) {
        typeParams.set(type as GenerationType, new Set())
      }
      for (const p of variant.supportedParams) {
        typeParams.get(type as GenerationType)!.add(p)
      }
    }
  }

  const categories: Record<string, Array<GenerationTypeInfo>> = {}
  for (const [type, data] of typeMap.entries()) {
    const category: GenerationCategory = GENERATION_TYPE_TO_CATEGORY[type]
    if (!categories[category]) categories[category] = []
    categories[category].push({
      type,
      label: GENERATION_TYPE_LABELS[type] ?? type,
      category,
      models: data.models,
      modelCount: data.models.length,
      families: data.families,
      params: [...(typeParams.get(type) ?? [])],
    })
  }

  return {
    categories: Object.entries(categories).map(([cat, types]) => ({ category: cat, types })),
    totalTypes: typeMap.size,
  }
}

type CanonicalParamType = 'string' | 'number' | 'boolean' | 'enum'

export interface ParamInfo {
  name: string
  type: CanonicalParamType
  description: string
  category: string
  enumValues?: Array<string>
  default?: string
  usedByModels: Array<string>
  usedByTypes: Array<GenerationType>
}

export interface ParametersResult {
  total: number
  params: Array<ParamInfo>
}

// Hand-authored catalog of what each canonical parameter name means — mirrors
// the provider-specific `modelSettings` fields models actually accept.
// Kept here (not per-model) since most parameters are shared across models.
const PARAM_CATALOG: Record<
  string,
  { description: string; category: string; type: CanonicalParamType; enumValues?: Array<string>; default?: string }
> = {
  prompt: { description: 'The main generation prompt describing the desired output', category: 'Prompting', type: 'string' },
  negative_prompt: { description: 'What to exclude from the generated output', category: 'Prompting', type: 'string' },
  prompt_extend: { description: 'Auto-enhance or extend the prompt for better results', category: 'Prompting', type: 'boolean' },

  image_input: { description: 'Single image input URL or data', category: 'Media Inputs', type: 'string' },
  image_inputs: { description: 'Multiple image input URLs', category: 'Media Inputs', type: 'string' },
  image_url: { description: 'Single image URL (used by Kling models)', category: 'Media Inputs', type: 'string' },
  image_urls: { description: 'Array of image URLs', category: 'Media Inputs', type: 'string' },
  input_urls: { description: 'Input URLs for various operations', category: 'Media Inputs', type: 'string' },
  video_input: { description: 'Single video input URL', category: 'Media Inputs', type: 'string' },
  video_inputs: { description: 'Multiple video input URLs', category: 'Media Inputs', type: 'string' },
  audio_input: { description: 'Audio input URL or data', category: 'Media Inputs', type: 'string' },
  audio_inputs: { description: 'Multiple audio input URLs', category: 'Media Inputs', type: 'string' },
  reference_images: { description: 'Reference images for style/character guidance', category: 'Media Inputs', type: 'string' },
  reference_image_urls: { description: 'Reference image URLs (Seedance specific)', category: 'Media Inputs', type: 'string' },
  reference_video_urls: { description: 'Reference video URLs (Seedance specific)', category: 'Media Inputs', type: 'string' },
  reference_audio_urls: { description: 'Reference audio URLs (Seedance specific)', category: 'Media Inputs', type: 'string' },
  first_frame_url: { description: 'First frame URL for video generation (Seedance)', category: 'Media Inputs', type: 'string' },
  last_frame_url: { description: 'Last frame URL for video generation (Seedance)', category: 'Media Inputs', type: 'string' },
  video_urls: { description: 'Video URLs array (Kling Motion Control)', category: 'Media Inputs', type: 'string' },
  character_orientation: { description: 'Character orientation mode (Kling Motion Control)', category: 'Media Inputs', type: 'enum', enumValues: ['image', 'video'] },

  aspect_ratio: { description: 'Output aspect ratio', category: 'Output', type: 'enum', enumValues: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'], default: '1:1' },
  ratio: { description: 'Alternative aspect ratio parameter (some providers)', category: 'Output', type: 'enum', enumValues: ['16:9', '9:16', '1:1', '4:3'] },
  resolution: { description: 'Output resolution tier', category: 'Output', type: 'enum', enumValues: ['480p', '720p', '1080p', '4K'], default: '720p' },
  image_resolution: { description: 'Image-specific resolution tier (Seedream)', category: 'Output', type: 'enum', enumValues: ['1K', '2K', '4K'] },
  duration: { description: 'Output duration in seconds (video only)', category: 'Output', type: 'number', default: '5' },
  output_format: { description: 'Output file format', category: 'Output', type: 'enum', enumValues: ['png', 'jpg', 'webp', 'mp4', 'mov'], default: 'png' },
  size: { description: 'Image size (legacy, used by DALL-E)', category: 'Output', type: 'string' },
  image_size: { description: 'Image size specification', category: 'Output', type: 'string' },
  target_resolution: { description: 'Target resolution for upscaling', category: 'Output', type: 'string' },

  seed: { description: 'Random seed for reproducible generation', category: 'Generation Control', type: 'number' },
  guidance_scale: { description: 'CFG scale — how closely to follow the prompt (1-20)', category: 'Generation Control', type: 'number', default: '7.5' },
  guidance: { description: 'Alternative name for guidance_scale', category: 'Generation Control', type: 'number' },
  num_images: { description: 'Number of images to generate', category: 'Generation Control', type: 'number', default: '1' },
  max_images: { description: 'Maximum images (Seedream 4.0)', category: 'Generation Control', type: 'number' },
  num_outputs: { description: 'Number of outputs (alternative naming)', category: 'Generation Control', type: 'number' },
  steps: { description: 'Number of diffusion steps (higher = better quality, slower)', category: 'Generation Control', type: 'number' },
  intensity: { description: 'Effect intensity (0-1)', category: 'Generation Control', type: 'number' },
  quality: { description: 'Quality level', category: 'Generation Control', type: 'enum', enumValues: ['low', 'medium', 'high', 'basic', 'standard'], default: 'medium' },
  strength: { description: 'Effect strength for image-to-image (0-1)', category: 'Generation Control', type: 'number', default: '0.8' },
  mode: { description: 'Generation mode — switches model behavior', category: 'Generation Control', type: 'string' },
  style: { description: 'Style preset or reference', category: 'Generation Control', type: 'string' },
  enable_sequential: { description: 'Enable sequential image generation (Wan Image)', category: 'Generation Control', type: 'boolean' },
  thinking_mode: { description: 'Enable thinking mode for higher quality (Wan Image)', category: 'Generation Control', type: 'boolean' },
  enable_pro: { description: 'Enable pro/quality mode (Grok Imagine)', category: 'Generation Control', type: 'boolean' },
  web_search: { description: 'Use online search to enhance prompt', category: 'Generation Control', type: 'boolean' },

  fps: { description: 'Frames per second', category: 'Video', type: 'number', default: '24' },
  sound: { description: 'Enable/disable sound in video', category: 'Video', type: 'boolean', default: 'false' },
  generate_audio: { description: 'Generate AI audio synchronized with video', category: 'Video', type: 'boolean' },
  camera_movement: { description: 'Camera movement type', category: 'Video', type: 'string' },
  motion_type: { description: 'Motion/animation type', category: 'Video', type: 'string' },
  motion_intensity: { description: 'Strength of motion effect', category: 'Video', type: 'number' },

  task_id: { description: 'Task ID referencing previously generated content (Grok)', category: 'Advanced', type: 'string' },
  index: { description: 'Index for selecting output from task_id (Grok)', category: 'Advanced', type: 'number' },
  watermark: { description: 'Add or remove watermark', category: 'Advanced', type: 'boolean' },
  nsfw_checker: { description: 'Enable NSFW content filtering', category: 'Advanced', type: 'boolean' },
  scale: { description: 'Upscale factor', category: 'Advanced', type: 'number' },
}

export function buildParameters(): ParametersResult {
  const paramMap = new Map<string, { models: Set<string>; types: Set<GenerationType> }>()

  for (const [modelId, config] of Object.entries(GENERATION_MODEL_CATALOG)) {
    for (const [type, variant] of Object.entries(config.variants)) {
      if (!variant) continue
      for (const param of variant.supportedParams) {
        if (!paramMap.has(param)) paramMap.set(param, { models: new Set(), types: new Set() })
        paramMap.get(param)!.models.add(modelId)
        paramMap.get(param)!.types.add(type as GenerationType)
      }
    }
  }

  const params: Array<ParamInfo> = Object.entries(PARAM_CATALOG).map(([name, info]) => {
    const usage = paramMap.get(name)
    return {
      name,
      type: info.type,
      description: info.description,
      category: info.category,
      enumValues: info.enumValues,
      default: info.default,
      usedByModels: usage ? [...usage.models].sort() : [],
      usedByTypes: usage ? [...usage.types] : [],
    }
  })

  return { total: params.length, params }
}
